import { FIELD_MAX_LENGTHS } from '@/shared/constants';
import {
  Button,
  Dialog,
  Field,
  Text,
} from '@noorden-insurtech/alba-design-system';
import { Mail, SquarePen } from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { z } from 'zod';
import { normalizeEmail } from '@/shared/utils/normalize-email.util';

type SendEmailDialogValues = {
  brokerEmail: string;
  proponentEmail: string;
};

interface SendEmailDialogProps {
  isOpen: boolean;
  onClose: () => void;
  // eslint-disable-next-line no-unused-vars
  onConfirm: (values: SendEmailDialogValues) => void | Promise<void>;
  defaultBrokerEmail?: string;
  defaultProponentEmail?: string;
  isLoading?: boolean;
}

export function SendEmailDialog({
  isOpen,
  onClose,
  onConfirm,
  defaultBrokerEmail = '',
  defaultProponentEmail = '',
  isLoading = false,
}: SendEmailDialogProps) {
  const proponentInputRef = useRef<HTMLInputElement | null>(null);
  const brokerInputRef = useRef<HTMLInputElement | null>(null);

  const [brokerEmail, setBrokerEmail] = useState(defaultBrokerEmail);
  const [proponentEmail, setProponentEmail] = useState(defaultProponentEmail);
  const [isBrokerEditable, setIsBrokerEditable] = useState(false);
  const [touched, setTouched] = useState<{
    broker: boolean;
    proponent: boolean;
  }>({ broker: false, proponent: false });

  useEffect(() => {
    if (!isOpen) return;
    setBrokerEmail(defaultBrokerEmail);
    setProponentEmail(defaultProponentEmail);
    setIsBrokerEditable(false);
    setTouched({ broker: false, proponent: false });

    queueMicrotask(() => {
      proponentInputRef.current?.focus();
    });
  }, [defaultBrokerEmail, defaultProponentEmail, isOpen]);

  const emailSchema = useMemo(() => z.string().min(1).email(), []);

  const brokerEmailError = useMemo(() => {
    const result = emailSchema.safeParse(brokerEmail.trim());
    if (result.success) return '';
    if (!touched.broker) return '';
    return 'E-mail inválido';
  }, [brokerEmail, emailSchema, touched.broker]);

  const proponentEmailError = useMemo(() => {
    const result = emailSchema.safeParse(proponentEmail.trim());
    if (result.success) return '';
    if (!touched.proponent) return '';
    return 'E-mail inválido';
  }, [emailSchema, proponentEmail, touched.proponent]);

  const isFormValid =
    emailSchema.safeParse(brokerEmail.trim()).success &&
    emailSchema.safeParse(proponentEmail.trim()).success;

  const handleConfirm = async () => {
    setTouched({ broker: true, proponent: true });
    if (!isFormValid || isLoading) return;
    await onConfirm({
      brokerEmail: normalizeEmail(brokerEmail),
      proponentEmail: normalizeEmail(proponentEmail),
    });
  };

  return (
    <Dialog.Root open={isOpen} onOpenChange={onClose}>
      <Dialog.Backdrop />
      <Dialog.Positioner>
        <Dialog.Content className='w-[640px] max-w-[90vw] rounded-[24px] bg-[#F9F9F9] p-0 shadow-xl'>
          <div className='flex flex-col items-start gap-8 p-6'>
            <Dialog.Header className='w-full p-0'>
              <Dialog.Title className='w-full'>
                <div className='flex w-full items-center justify-between gap-3'>
                  <div className='flex items-center gap-2'>
                    <Mail className='h-6 w-6 text-alba-black-500' />
                    <Text className='text-lg font-bold text-alba-black-500'>
                      Mandar por E-mail
                    </Text>
                  </div>
                  <Dialog.CloseButton onClick={onClose} />
                </div>
              </Dialog.Title>
            </Dialog.Header>

            <Dialog.Body className='w-full p-0'>
              <div className='flex flex-col items-start gap-8 w-full'>
                <div className='flex flex-col items-start gap-2 w-full'>
                  <Text className='text-sm text-alba-gray-600'>
                    O PDF da cotação será gerado para envio.
                  </Text>
                  <Text className='text-sm text-alba-gray-600'>
                    Indique os e-mails que deverão receber a cotação.
                  </Text>
                </div>

                <div className='flex flex-col items-start gap-6 w-full'>
                  <Field.Root invalid={Boolean(brokerEmailError)}>
                    <Field.Label className='text-sm font-bold text-alba-black-500'>
                      E-mail Corretor
                    </Field.Label>

                    <div className='relative w-full'>
                      <Field.Input
                        ref={brokerInputRef}
                        type='email'
                        value={brokerEmail}
                        onChange={e =>
                          setBrokerEmail(e.target.value.toLowerCase())
                        }
                        onBlur={() =>
                          setTouched(prev => ({ ...prev, broker: true }))
                        }
                        placeholder='corretor@email.com'
                        readOnly={!isBrokerEditable}
                        maxLength={FIELD_MAX_LENGTHS.email}
                        className={`h-10 w-full rounded-xl px-5 pr-12 ${
                          isBrokerEditable
                            ? 'border border-alba-gray-400 bg-white text-alba-gray-600'
                            : '!border-0 bg-[#E7E8E9] text-[#9FA4A6]'
                        }`}
                      />
                      <button
                        type='button'
                        aria-label={
                          isBrokerEditable
                            ? 'Desativar edição do e-mail do corretor'
                            : 'Editar e-mail do corretor'
                        }
                        onClick={() => {
                          setIsBrokerEditable(prev => !prev);
                          queueMicrotask(() => {
                            brokerInputRef.current?.focus();
                          });
                        }}
                        className='absolute right-4 top-1/2 -translate-y-1/2 rounded-md p-1 hover:bg-black/5'
                      >
                        <SquarePen
                          className={`h-6 w-6 ${
                            isBrokerEditable
                              ? 'text-alba-blue-500'
                              : 'text-[#9FA4A6]'
                          }`}
                          strokeWidth={1.6}
                        />
                      </button>
                    </div>

                    {brokerEmailError && (
                      <Field.ErrorText>{brokerEmailError}</Field.ErrorText>
                    )}
                  </Field.Root>

                  <Field.Root invalid={Boolean(proponentEmailError)}>
                    <Field.Label className='text-sm font-bold text-alba-black-500'>
                      E-mail Proponente
                    </Field.Label>
                    <Field.Input
                      ref={proponentInputRef}
                      type='email'
                      value={proponentEmail}
                      onChange={e =>
                        setProponentEmail(e.target.value.toLowerCase())
                      }
                      onBlur={() =>
                        setTouched(prev => ({ ...prev, proponent: true }))
                      }
                      placeholder='proponente@email.com'
                      maxLength={FIELD_MAX_LENGTHS.email}
                      className='h-10 w-full rounded-xl border border-alba-gray-400 bg-white px-5 text-base text-alba-gray-600'
                    />
                    {proponentEmailError && (
                      <Field.ErrorText>{proponentEmailError}</Field.ErrorText>
                    )}
                  </Field.Root>
                </div>
              </div>
            </Dialog.Body>

            <Dialog.Footer className='w-full p-0'>
              <div className='flex w-full items-center justify-end gap-6'>
                <Dialog.CloseTrigger asChild>
                  <Button variant='ghost' size='md'>
                    Cancelar
                  </Button>
                </Dialog.CloseTrigger>
                <Button
                  variant='primary'
                  size='md'
                  loading={isLoading}
                  disabled={isLoading || !isFormValid}
                  onClick={handleConfirm}
                >
                  Enviar
                </Button>
              </div>
            </Dialog.Footer>
          </div>
        </Dialog.Content>
      </Dialog.Positioner>
    </Dialog.Root>
  );
}
